/**
 * Site-visit outcome helpers shared by the lead page, the Meetings tab and the
 * sales dashboard. "Site Visit Scheduled" is the product name for the stage the
 * backend stores as `Demo Scheduled` / pstatus `Demo`.
 */

export const PENDING_SITE_VISITS_QUERY_KEY = ['pendingSiteVisits'];

export const OUTCOME_CHOICES = [
  { id: 'success', label: 'Visit done', hint: 'The lead visited the site' },
  { id: 'abort', label: 'Abort', hint: 'No-show, not interested or cancelled' },
  { id: 'reschedule', label: 'Reschedule', hint: 'Move the visit to a new time' }
];

export const ABORT_REASONS = [
  {
    id: 'no_show',
    label: 'No-show',
    hint: 'The lead did not turn up. Stays scheduled so you can rebook.'
  },
  {
    id: 'not_interested',
    label: 'Not interested',
    hint: 'Closes the lead as Closed Lost.'
  },
  {
    id: 'cancelled_by_us',
    label: 'Cancelled by us',
    hint: 'We called it off. Stays scheduled so you can rebook.'
  }
];

const OUTCOME_BADGES = {
  success: { label: 'Visit done', className: 'bg-green-100 text-green-800' },
  no_show: { label: 'No-show', className: 'bg-amber-100 text-amber-800' },
  not_interested: { label: 'Not interested', className: 'bg-red-100 text-red-700' },
  cancelled_by_us: { label: 'Cancelled by us', className: 'bg-slate-200 text-slate-700' },
  rescheduled: { label: 'Rescheduled', className: 'bg-indigo-100 text-indigo-800' }
};

export function outcomeBadge(outcome) {
  return OUTCOME_BADGES[outcome] || null;
}

const pad = (n) => String(n).padStart(2, '0');

/** A date as the `YYYY-MM-DDTHH:mm` string a datetime-local input expects. */
export function toDateTimeLocal(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** 11:00 local time, `daysAhead` days from now — the default follow-up slot. */
export function defaultFollowUpInput(daysAhead, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + daysAhead);
  d.setHours(11, 0, 0, 0);
  return toDateTimeLocal(d);
}

/** Two days after a visit, one day after anything that still needs a rebooking. */
export function defaultDaysAhead(outcome) {
  return outcome === 'success' ? 2 : 1;
}

/** An abort that keeps the lead scheduled, so it needs a rebooking. */
export function needsRebooking(form) {
  return (
    form.outcome === 'abort' &&
    (form.abortReason === 'no_show' || form.abortReason === 'cancelled_by_us')
  );
}

function toIso(localValue) {
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Turn the form into the API payload, or return the first problem to show.
 * `pickerAvailable` is whether this builder can send the WhatsApp time picker.
 */
export function buildOutcomePayload(form, { pickerAvailable = false, now = new Date() } = {}) {
  if (!form.outcome) return { error: 'Choose Visit done, Abort or Reschedule' };

  const note = (form.note || '').trim();
  if (form.outcome === 'abort') {
    if (!form.abortReason) return { error: 'Choose why the visit did not happen' };
    if (form.abortReason === 'not_interested' && !note) {
      return { error: 'Add a reason why the lead is not interested' };
    }
  }

  const payload = { outcome: form.outcome };
  if (form.outcome === 'abort') payload.abort_reason = form.abortReason;
  if (note) payload.note = note;

  if (form.outcome === 'reschedule') {
    const mode = pickerAvailable ? form.rescheduleMode : 'time';
    if (mode === 'whatsapp') {
      payload.send_whatsapp_picker = true;
      return { payload };
    }
    const at = form.rescheduleAt ? new Date(form.rescheduleAt) : null;
    if (!at || Number.isNaN(at.getTime())) return { error: 'Pick the new visit date and time' };
    if (at.getTime() <= now.getTime()) return { error: 'The new visit time must be in the future' };
    payload.reschedule_at = at.toISOString();
    return { payload };
  }

  const closesLead = form.outcome === 'abort' && form.abortReason === 'not_interested';
  if (!closesLead && form.nextFollowUpAt) {
    const iso = toIso(form.nextFollowUpAt);
    if (!iso) return { error: 'Pick a valid next follow-up date' };
    payload.next_follow_up_at = iso;
  }
  if (needsRebooking(form) && pickerAvailable && form.sendWhatsAppPicker) {
    payload.send_whatsapp_picker = true;
  }
  return { payload };
}

/** A booked meeting whose time has come and nobody has recorded an outcome for. */
export function awaitsOutcome(meeting, now = new Date()) {
  if (!meeting || meeting.status !== 'active' || meeting.outcome) return false;
  const start = new Date(meeting.start_time);
  return !Number.isNaN(start.getTime()) && start.getTime() <= now.getTime();
}

export function formatVisitTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}
